import { useMutation } from '@apollo/client';
import type { AttestMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { AttestDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import { useAttestRecordTRPC } from './useAttestRecordTRPC';

/**
 * Flag-gated wrapper for the `attestRecord` mutation. Defaults to the
 * legacy Apollo mutation and delegates to the tRPC variant when the
 * `trpc` feature flag is enabled. Consumers import THIS, never the
 * `*TRPC` variant directly.
 */
export const useAttestRecord = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [attestGraphQL, graphqlState] = useMutation(AttestDocument);

  const trpcMutation = useAttestRecordTRPC();

  const attest = async (input: { Id: string }): Promise<AttestMutation> => {
    if (trpcEnabled) {
      return trpcMutation.attest(input);
    }

    const result = await attestGraphQL({
      variables: { Id: input.Id },
    });
    if (!result.data) {
      throw new Error('Failed to attest record');
    }

    return result.data;
  };

  return {
    attest,
    loading: trpcEnabled ? trpcMutation.loading : graphqlState.loading,
    error: trpcEnabled ? trpcMutation.error : graphqlState.error,
  };
};
